<template>
  <div class="fa-status" :class="'fa-status--' + tone">

    <!-- Pastille vivante. L'animation ne tourne QUE quand l'agent est en
         ligne : un point qui pulse sur un agent mort serait un mensonge
         visuel, et c'est précisément l'information qu'on vient chercher. -->
    <div class="fa-status__dot-wrap">
      <span class="fa-status__dot"></span>
      <span v-if="tone === 'online'" class="fa-status__ring"></span>
    </div>

    <div class="fa-status__body">
      <div class="fa-status__line">
        <span class="fa-status__badge">{{ badge }}</span>
        <span v-if="agent" class="fa-status__host">{{ agent.name }}</span>
      </div>

      <!-- Le temps écoulé se recalcule tout seul : un « 10 s ago » figé sur une
           page laissée ouverte une heure est pire que pas d'information. -->
      <div class="fa-status__detail">{{ detail }}</div>

      <div v-if="agent && showPaths" class="fa-status__paths">
        <div><span class="fa-status__k">writes</span> <code>{{ agent.bonuri || '?' }}</code></div>
        <div><span class="fa-status__k">reads</span> <code>{{ agent.raspuns || '?' }}</code></div>
        <div v-if="agent.version || agent.last_command" class="fa-status__meta">
          <span v-if="agent.version">v{{ agent.version }}</span>
          <span v-if="agent.last_command"> · last command: {{ agent.last_command }}</span>
        </div>
      </div>

      <div v-if="agent && agent.last_error" class="fa-status__err">{{ agent.last_error }}</div>

      <!-- LA CAISSE, distincte de l'agent. Le 18/09 l'agent etait « en ligne »
           pendant que la caisse, passee a une autre IP, ne recevait plus rien :
           l'ecran doit dire les deux separement. -->
      <div v-if="register && register.level !== 'unknown'" class="fa-status__register" :class="'fa-status__register--' + register.level">
        <span class="fa-status__k">register</span>
        <span>{{ register.message }}</span>
      </div>
      <div v-else-if="agent && !agent.stale && !agent.register_probe" class="fa-status__meta">
        This agent version does not monitor the register itself — update it to v1.1.
      </div>

      <div v-if="!agent && showHelp" class="fa-status__help">
        Install <code>XYLO-X/CasaMarcat</code> on the computer wired to the register, then run
        <code>node bin/casamarcat.js ping</code>.
      </div>
    </div>
  </div>
</template>

<script>
/**
 * Indicateur d'activité de l'agent CasaMarcat.
 *
 * Partagé entre l'écran de configuration et la liste des bons : un même état
 * affiché différemment d'un écran à l'autre rend le diagnostic impossible.
 *
 * `agent` est la charge renvoyée par GET /api/fiscal/counts (clé `agent`),
 * ou null si l'agent n'a jamais donné signe de vie.
 */
export default {
  props: {
    agent: { type: Object, default: null },
    // Des bons attendent-ils ? Un agent hors ligne SANS file est une gêne ;
    // avec une file, c'est une panne qui bloque un encaissement.
    pending: { type: Number, default: 0 },
    // État de la CAISSE calculé par le serveur (clé `register` de /fiscal/counts).
    register: { type: Object, default: null },
    showPaths: { type: Boolean, default: true },
    showHelp: { type: Boolean, default: true },
  },
  data() {
    return { now: Date.now(), timer: null };
  },
  computed: {
    /** Secondes écoulées, recalculées localement à partir de l'instant du chargement. */
    secondsAgo() {
      if (!this.agent) return null;
      const base = Number(this.agent.seconds_ago);
      if (!Number.isFinite(base)) return null;
      return base + Math.round((this.now - this.mountedAt) / 1000);
    },
    stale() {
      const s = this.secondsAgo;
      // 5 min, le même seuil que le serveur. Le recalculer ici permet au badge
      // de passer hors ligne sans attendre un rechargement de page.
      return s === null || s > 300;
    },
    tone() {
      if (!this.agent) return 'never';
      if (this.agent.status === 'error') return 'error';
      return this.stale ? 'offline' : 'online';
    },
    badge() {
      return {
        online: 'ONLINE',
        offline: 'OFFLINE',
        error: 'PROBLEM',
        never: 'NOT INSTALLED',
      }[this.tone];
    },
    detail() {
      if (!this.agent) return 'The agent has never reported in — nothing can print.';

      const ago = this.agoText;
      if (this.tone === 'online') return `Responding — last seen ${ago}.`;
      if (this.tone === 'error') return `Running, but reporting a problem. Last seen ${ago}.`;

      // Hors ligne : on dit la conséquence, pas seulement l'état.
      return this.pending > 0
        ? `Not responding since ${ago}. ${this.pending} receipt(s) are waiting and will not print.`
        : `Not responding since ${ago}. Nothing is waiting, so nothing is blocked yet.`;
    },
    agoText() {
      const s = this.secondsAgo;
      if (s === null) return 'never';
      if (s < 10) return 'just now';
      if (s < 60) return `${s} s ago`;
      if (s < 3600) return `${Math.round(s / 60)} min ago`;
      if (s < 86400) return `${Math.round(s / 3600)} h ago`;
      return `${Math.round(s / 86400)} day(s) ago`;
    },
  },
  created() {
    this.mountedAt = Date.now();
  },
  mounted() {
    // 5 s : assez pour que « just now » devienne « 30 s ago » sous les yeux,
    // sans rien demander au serveur — c'est un simple calcul local.
    this.timer = setInterval(() => { this.now = Date.now(); }, 5000);
  },
  beforeDestroy() {
    if (this.timer) clearInterval(this.timer);
  },
};
</script>

<style scoped>
.fa-status {
  display: flex;
  align-items: flex-start;
  padding: 1rem 1.15rem;
  border-radius: .6rem;
  border-left: 4px solid;
  background: rgba(0, 0, 0, .02);
}
.fa-status--online  { border-color: #28c76f; }
.fa-status--offline { border-color: #ea5455; }
.fa-status--error   { border-color: #ff9f43; }
.fa-status--never   { border-color: #b8c2cc; }

.fa-status__dot-wrap {
  position: relative;
  width: 14px; height: 14px;
  margin: .3rem .95rem 0 0;
  flex: 0 0 auto;
}
.fa-status__dot {
  position: absolute; inset: 0;
  border-radius: 50%;
  background: #b8c2cc;
}
.fa-status--online  .fa-status__dot { background: #28c76f; }
.fa-status--offline .fa-status__dot { background: #ea5455; }
.fa-status--error   .fa-status__dot { background: #ff9f43; }

/* Le halo ne pulse que sur un agent en ligne : c'est le seul cas où « ça
   bouge » est vrai. */
.fa-status__ring {
  position: absolute; inset: 0;
  border-radius: 50%;
  background: #28c76f;
  animation: fa-pulse 1.8s ease-out infinite;
}
@keyframes fa-pulse {
  0%   { transform: scale(1);   opacity: .55; }
  70%  { transform: scale(2.6); opacity: 0; }
  100% { transform: scale(2.6); opacity: 0; }
}
/* Respecte le réglage système : une animation qui tourne en boucle est un
   irritant réel pour qui a demandé moins de mouvement. */
@media (prefers-reduced-motion: reduce) {
  .fa-status__ring { animation: none; opacity: .35; }
}

.fa-status__body { min-width: 0; }
.fa-status__line { display: flex; align-items: center; flex-wrap: wrap; }
.fa-status__badge {
  font-size: .7rem; font-weight: 700; letter-spacing: .06em;
  padding: .12rem .5rem; border-radius: .3rem; color: #fff;
}
.fa-status--online  .fa-status__badge { background: #28c76f; }
.fa-status--offline .fa-status__badge { background: #ea5455; }
.fa-status--error   .fa-status__badge { background: #ff9f43; }
.fa-status--never   .fa-status__badge { background: #b8c2cc; }

.fa-status__host { margin-left: .6rem; font-weight: 600; }
.fa-status__detail { margin-top: .3rem; font-size: .85rem; }
.fa-status__paths { margin-top: .5rem; font-size: .75rem; opacity: .8; line-height: 1.5; }
.fa-status__k {
  display: inline-block; min-width: 3.2rem;
  text-transform: uppercase; font-size: .65rem; letter-spacing: .05em; opacity: .7;
}
.fa-status__meta { margin-top: .2rem; font-size: .7rem; opacity: .7; }
.fa-status__err { margin-top: .5rem; font-size: .8rem; color: #ea5455; }
.fa-status__help { margin-top: .4rem; font-size: .8rem; opacity: .85; }
.fa-status__register { margin-top: .6rem; font-size: .85rem; line-height: 1.4; }
.fa-status__register--ok     { color: #28c76f; }
.fa-status__register--danger { color: #ea5455; font-weight: 600; }
.fa-status__register--warning { color: #ff9f43; font-weight: 600; }
</style>
